import ky, { type HTTPError, KyInstance } from 'ky';
import { useHAlerts } from '../composables/use-alerts';
import { hCreateGenericErrorAlert } from '../utils/create-generic-alerts';
import { HExpectedApiError, apiErrorKey } from '../utils/expected-api-error';

const { hAddAlert } = useHAlerts();

/**
 * Ky 2 puts the parsed API body on `error.data` and consumes `error.response`.
 * Onify errors are JSON: `{ statusCode, message, error: { message, locale } }`.
 */
function getApiErrorPayload(error: HTTPError & { data?: unknown }): { body?: unknown; message?: string } {
  const body = error.data !== undefined ? error.data : undefined;
  if (body == null) return {};

  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return messageFromBody(parsed);
    } catch {
      return { body };
    }
  }

  if (typeof body === 'object') {
    return messageFromBody(body);
  }

  return { body };
}

function messageFromBody(body: unknown): { body: unknown; message?: string } {
  const record = body as { error?: { message?: unknown }; message?: unknown };
  const message = record?.error?.message || record?.message;
  return { body, message: typeof message === 'string' ? message : undefined };
}

function additionalInfoFromApiBody(body: unknown): object | undefined {
  if (body && typeof body === 'object') return body as object;
  if (typeof body === 'string' && body) return { response: body };
  return undefined;
}

// Onify API library uses Ky as the http client
const _helixHttpClient = ky.create({});

/* 
  FIXME: Find a better solution. As of now the problem stems from upstream where `ky` has not
  provided an easy way to catch `TimeoutError`
*/
/**
 * Enhances a Ky instance with additional error handling capabilities.
 *
 * @function
 * @name proxifyKyInstance
 * @description
 * This function takes a Ky instance and returns a proxied version of it. The proxy adds custom error handling to the instance's methods.
 * If an error occurs during a request, a generic error alert is created and added to the alert system.
 *
 * @param {KyInstance} instance - The Ky instance to be proxied.
 * @returns {KyInstance} - The proxied Ky instance with enhanced error handling.
 */
function proxifyKyInstance(instance: KyInstance): KyInstance {
  return new Proxy(instance, {
    apply: async function (_1, _2, params: Parameters<typeof instance>) {
      const requestOptions = params[1] as { context?: { silentErrors?: boolean; expectedErrorKeys?: string[] } } | undefined;
      return instance(...params).catch(async (error) => {
        // Background/polled requests opted into `silentErrors` log instead of alerting.
        // Read from Ky 2 `context` on the original call args so TimeoutError and network
        // TypeError (which may have no `error.request`) are covered too.
        if (requestOptions?.context?.silentErrors) {
          console.warn('[http] Silenced request failure:', error);
          return;
        }

        if (error.response) {
          const payload = getApiErrorPayload(error);

          // An API error the caller expects goes back to the caller instead of the alert.
          const key = apiErrorKey(payload.body);
          if (key && requestOptions?.context?.expectedErrorKeys?.includes(key)) {
            throw new HExpectedApiError(key, payload.message);
          }

          const additionalInfo = additionalInfoFromApiBody(payload.body);
          const alert = hCreateGenericErrorAlert(error, additionalInfo);
          if (payload.message) {
            alert.title = payload.message;
          }
          if (alert.details) {
            // Ky stack traces are not useful next to the API payload.
            alert.details.description = undefined;
            if (payload.message) {
              alert.details.title = payload.message;
            }
          }
          hAddAlert(alert);
          return;
        }

        hAddAlert(hCreateGenericErrorAlert(error, undefined));
      });
    },
    get: function (target, prop, receiver) {
      const key = prop as keyof KyInstance;

      if (key === 'create' || key === 'extend') {
        const instanceFunc = Reflect.get(target, key, receiver);

        return function (...parameters: Parameters<KyInstance['extend']>) {
          return proxifyKyInstance((instanceFunc as (options?: Parameters<KyInstance['extend']>[0]) => KyInstance)(parameters[0]));
        };
      }

      return Reflect.get(target, key, receiver);
    },
  });
}

const hHttpClient: KyInstance = proxifyKyInstance(_helixHttpClient);

export { hHttpClient };
